import { useState, useCallback, useEffect, useRef } from 'react'

interface MediaPermissions {
  microphone: boolean
  camera: boolean
  screen: boolean
}

interface MediaStreams {
  camera: MediaStream | null
  screen: MediaStream | null
  microphone: MediaStream | null
}

interface UseMediaPermissionsReturn {
  permissions: MediaPermissions
  streams: MediaStreams
  isCheckingPermissions: boolean
  requestAllPermissions: () => Promise<void>
  requestScreenShare: () => Promise<MediaStream | null>
  initializeStreamsFromPermissions: (includeScreen?: boolean) => Promise<void>
  stopAllStreams: () => void
  stopStream: (streamType: 'camera' | 'screen') => void
  setOnScreenSharingEnded: (callback: () => void) => void
}

const RECORDING_CONFIG = {
  screen: {
    video: {
      width: { ideal: 1600 },  // getDisplayMedia doesn't support min constraints
      height: { ideal: 900 },  // getDisplayMedia doesn't support min constraints
      frameRate: { ideal: 30 },
      displaySurface: "monitor" // Prefer full screen, but allow fallback
    },
    audio: true
  },
  camera: {
    video: {
      width: { ideal: 960, min: 640 },    // Reduced from 1280 to balance quality/size
      height: { ideal: 540, min: 480 },   // Reduced from 720 to balance quality/size
      frameRate: { ideal: 30, min: 24 }
    },
    audio: true
  }
}

export const useMediaPermissions = (): UseMediaPermissionsReturn => {
  // Load permissions from localStorage on initialization
  const [permissions, setPermissions] = useState<MediaPermissions>(() => {
    try {
      const saved = localStorage.getItem('snwolley-media-permissions')
      return saved ? JSON.parse(saved) : {
        microphone: false,
        camera: false,
        screen: false
      }
    } catch {
      return {
        microphone: false,
        camera: false,
        screen: false
      }
    }
  })

  const [streams, setStreams] = useState<MediaStreams>({
    camera: null,
    screen: null,
    microphone: null
  })

  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false)
  const [hasInitialized, setHasInitialized] = useState(false)
  const [isInitializingStreams, setIsInitializingStreams] = useState(false)
  const onScreenSharingEndedRef = useRef<(() => void) | null>(null)

  // Save permissions to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('snwolley-media-permissions', JSON.stringify(permissions))
  }, [permissions])

  // Don't auto-initialize streams on mount
  useEffect(() => {
    if (!hasInitialized) {
      setHasInitialized(true)
      console.log('MediaPermissions hook initialized, permissions:', permissions)
    }
  }, [hasInitialized, permissions])

  // Method to silently re-acquire streams using stored permissions
  // This should work without prompting if permissions were already granted
  const initializeStreamsFromPermissions = useCallback(async (includeScreen: boolean = false) => {
    if (isInitializingStreams) {
      console.log('Already initializing streams, skipping duplicate call')
      return
    }

    setIsInitializingStreams(true)
    console.log('Attempting to silently re-acquire streams from stored permissions:', permissions)

    try {
      // Only attempt if we have stored permissions
      if (permissions.camera && permissions.microphone && !streams.camera) {
        console.log('Attempting to re-acquire camera stream...')
        try {
          const cameraStream = await navigator.mediaDevices.getUserMedia(RECORDING_CONFIG.camera)
          console.log('Successfully re-acquired camera stream')
          setStreams(prev => ({ ...prev, camera: cameraStream, microphone: cameraStream }))
        } catch (error) {
          console.warn('Failed to silently re-acquire camera stream:', error)
        }
      }

      // Only request screen sharing if explicitly requested
      if (includeScreen && permissions.screen && !streams.screen) {
        console.log('Re-requesting screen sharing (browser security requirement)...')
        try {
          const screenStream = await navigator.mediaDevices.getDisplayMedia(RECORDING_CONFIG.screen)
          console.log('Successfully re-acquired screen stream')
          setStreams(prev => ({ ...prev, screen: screenStream }))

          // Set up event listener for when user stops sharing
          const videoTrack = screenStream.getVideoTracks()[0]
          videoTrack.addEventListener('ended', () => {
            console.log('Screen sharing ended by user')
            setPermissions(prev => ({ ...prev, screen: false }))
            setStreams(prev => ({ ...prev, screen: null }))
            // Call the callback if set (e.g., to end interview)
            if (onScreenSharingEndedRef.current) {
              console.log('Calling screen sharing ended callback')
              onScreenSharingEndedRef.current()
            } else {
              console.log('No screen sharing ended callback set')
            }
          })
        } catch (error) {
          console.warn('User declined screen sharing on interview page:', error)
          // Reset screen permission since user declined
          setPermissions(prev => ({ ...prev, screen: false }))
        }
      }
    } catch (error) {
      console.error('Error in initializeStreamsFromPermissions:', error)
    } finally {
      setIsInitializingStreams(false)
    }
  }, [permissions, streams, isInitializingStreams])

  const stopStream = useCallback((streamType: 'camera' | 'screen') => {
    const stream = streams[streamType]
    if (stream) {
      stream.getTracks().forEach(track => {
        track.stop()
      })
      setStreams(prev => ({
        ...prev,
        [streamType]: null
      }))
    }
  }, [streams])

  const stopAllStreams = useCallback(() => {
    stopStream('camera')
    stopStream('screen')
  }, [stopStream])


  const requestAllPermissions = useCallback(async () => {
    setIsCheckingPermissions(true)
    
    try {
      // Request camera and microphone together for better UX
      const cameraStream = await navigator.mediaDevices.getUserMedia(RECORDING_CONFIG.camera)
      
      setPermissions(prev => ({ 
        ...prev, 
        microphone: true,
        camera: true 
      }))
      
      setStreams(prev => ({
        ...prev,
        camera: cameraStream,
        microphone: cameraStream
      }))

      // Try to pre-request screen sharing (user might deny, which is ok)
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia(RECORDING_CONFIG.screen)
        setPermissions(prev => ({ ...prev, screen: true }))
        setStreams(prev => ({
          ...prev,
          screen: screenStream
        }))
      } catch (screenError) {
        console.warn('Screen sharing permission not granted yet:', screenError)
        setPermissions(prev => ({ ...prev, screen: false }))
      }

    } catch (error) {
      console.error('Failed to get camera/microphone permissions:', error)
      setPermissions(prev => ({ 
        ...prev, 
        microphone: false,
        camera: false 
      }))
      throw error
    } finally {
      setIsCheckingPermissions(false)
    }
  }, [])

  const requestScreenShare = useCallback(async (): Promise<MediaStream | null> => {
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia(RECORDING_CONFIG.screen)
      
      const videoTrack = screenStream.getVideoTracks()[0]
      const settings = videoTrack.getSettings()
      console.log('Screen sharing started with displaySurface:', settings.displaySurface)

      if (settings.displaySurface !== 'monitor') {
        screenStream.getTracks().forEach(track => track.stop())
        throw new Error('Full screen capture is required. Please select "Entire Screen".')
      }

      // Set up event listener for when user stops sharing
      videoTrack.addEventListener('ended', () => {
        console.log('Screen sharing ended by user')
        setPermissions(prev => ({ ...prev, screen: false }))
        setStreams(prev => ({ ...prev, screen: null }))
        // Call the callback if set (e.g., to end interview)
        if (onScreenSharingEndedRef.current) {
          console.log('Calling screen sharing ended callback')
          onScreenSharingEndedRef.current()
        } else {
          console.log('No screen sharing ended callback set')
        }
      })

      setPermissions(prev => ({ ...prev, screen: true }))
      setStreams(prev => ({ ...prev, screen: screenStream }))
      
      return screenStream
    } catch (error) {
      console.error('Screen sharing failed:', error)
      setPermissions(prev => ({ ...prev, screen: false }))
      throw error
    }
  }, [])

  const setOnScreenSharingEndedCallback = useCallback((callback: () => void) => {
    console.log('Setting screen sharing ended callback')
    onScreenSharingEndedRef.current = callback
  }, [])

  return {
    permissions,
    streams,
    isCheckingPermissions,
    requestAllPermissions,
    requestScreenShare,
    initializeStreamsFromPermissions,
    stopAllStreams,
    stopStream,
    setOnScreenSharingEnded: setOnScreenSharingEndedCallback
  }
}